import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Student, TuitionMonthlyFee, TuitionPayment } from '@/types';
import { formatDate, formatNumber } from '@/lib/datetime';
import {
  addCycleMonths,
  cycleDueDate,
  cycleKey,
  cycleMonthLabel,
  dueDayLabel,
  excludedCycleKeysOf,
  isCycleExcluded,
  monthlyFeeAt,
  paidCycleKeysOf,
  parseCycleKey,
  resolveTuitionCycles,
  todayKey,
  type CyclePaymentStatus,
} from '@/lib/tuition';
import Field, { inputClass } from '@/components/base/Field';
import TuitionMonthlyFeePanel, {
  type MonthlyFeeInput,
  type MonthlyFeeRef,
} from './TuitionMonthlyFeePanel';
import TuitionPaymentHistoryPanel from './TuitionPaymentHistoryPanel';

interface TuitionSectionProps {
  student: Student;
  onUpdated: (updated: Student) => void;
}

const STATUS_META: Record<
  CyclePaymentStatus,
  { label: string; badge: string; icon: string }
> = {
  paid: {
    label: '결제완료',
    badge: 'bg-secondary-100 text-secondary-900',
    icon: 'ri-checkbox-circle-line',
  },
  overdue: {
    label: '미납',
    badge: 'bg-accent-100 text-accent-800',
    icon: 'ri-error-warning-line',
  },
  upcoming: {
    label: '결제 예정',
    badge: 'bg-background-200 text-foreground-700',
    icon: 'ri-time-line',
  },
  excluded: {
    label: '회차 제외',
    badge: 'bg-background-200 text-foreground-600',
    icon: 'ri-forbid-line',
  },
};

export default function TuitionSection({ student, onUpdated }: TuitionSectionProps) {
  const [monthlyFees, setMonthlyFees] = useState<TuitionMonthlyFee[]>([]);
  const [payments, setPayments] = useState<TuitionPayment[]>([]);
  const [loadingExtras, setLoadingExtras] = useState(true);
  const [dueDay, setDueDay] = useState(String(student.tuition_due_day || 10));
  const [dueOffset, setDueOffset] = useState(String(student.tuition_due_month_offset ?? 0));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paying, setPaying] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const now = new Date();
  const currentMonthKey = cycleKey(now.getFullYear(), now.getMonth() + 1);
  const [selectedKey, setSelectedKey] = useState(currentMonthKey);

  useEffect(() => {
    setDueDay(String(student.tuition_due_day || 10));
  }, [student.tuition_due_day]);

  useEffect(() => {
    setDueOffset(String(student.tuition_due_month_offset ?? 0));
  }, [student.tuition_due_month_offset]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(''), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  const loadExtras = useCallback(async () => {
    setLoadingExtras(true);
    try {
      const [feeRes, payRes] = await Promise.all([
        supabase
          .from('tuition_monthly_fees')
          .select('*')
          .eq('student_id', student.id)
          .order('year', { ascending: false })
          .order('month', { ascending: false }),
        supabase
          .from('tuition_payments')
          .select('*')
          .eq('student_id', student.id)
          .order('paid_at', { ascending: false }),
      ]);
      if (feeRes.error) throw feeRes.error;
      if (payRes.error) throw payRes.error;
      setMonthlyFees((feeRes.data as TuitionMonthlyFee[]) ?? []);
      setPayments((payRes.data as TuitionPayment[]) ?? []);
    } catch {
      setError('교습비 정보를 불러오지 못했어요.');
    } finally {
      setLoadingExtras(false);
    }
  }, [student.id]);

  useEffect(() => {
    loadExtras();
  }, [loadExtras]);

  const today = todayKey();
  const baseFee = student.tuition_fee || 0;
  const ownDueDay = student.tuition_due_day || 0;
  const ownOffset = student.tuition_due_month_offset ?? 0;
  const currentFee = monthlyFeeAt(monthlyFees, baseFee, now.getFullYear(), now.getMonth() + 1);
  const currentExcluded = isCycleExcluded(monthlyFees, now.getFullYear(), now.getMonth() + 1);

  const paidKeys = useMemo(() => paidCycleKeysOf(payments, ownOffset), [payments, ownOffset]);
  const excludedKeys = useMemo(() => excludedCycleKeysOf(monthlyFees), [monthlyFees]);
  const resolved = useMemo(
    () => resolveTuitionCycles(student.tuition_due_day, paidKeys, excludedKeys, ownOffset),
    [student.tuition_due_day, paidKeys, excludedKeys, ownOffset],
  );

  const status: CyclePaymentStatus =
    resolved?.status ?? (student.tuition_last_paid_at ? 'paid' : 'upcoming');
  const meta = STATUS_META[status];

  const collectKey = resolved?.collectKey ?? currentMonthKey;
  const collectDue = ownDueDay ? cycleDueDate(collectKey, ownDueDay, ownOffset) : null;
  const collectFee = monthlyFeeAt(
    monthlyFees,
    baseFee,
    parseCycleKey(collectKey).year,
    parseCycleKey(collectKey).month,
  );

  useEffect(() => {
    if (resolved?.collectKey) setSelectedKey(resolved.collectKey);
  }, [resolved?.collectKey]);

  const sel = parseCycleKey(selectedKey);
  const selDue = ownDueDay ? cycleDueDate(selectedKey, ownDueDay, ownOffset) : null;
  const selFee = monthlyFeeAt(monthlyFees, baseFee, sel.year, sel.month);
  const isSelPaid = paidKeys.includes(selectedKey);
  const isSelExcluded = excludedKeys.includes(selectedKey);
  const selStatus: CyclePaymentStatus = isSelPaid
    ? 'paid'
    : isSelExcluded
      ? 'excluded'
      : selDue && selDue < today
        ? 'overdue'
        : 'upcoming';
  const selMeta = STATUS_META[selStatus];

  const saveDueDay = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data, error: updateError } = await supabase
        .from('students')
        .update({
          tuition_due_day: dueDay ? Number(dueDay) : 0,
          tuition_due_month_offset: dueOffset === '-1' ? -1 : 0,
        })
        .eq('id', student.id)
        .select();
      if (updateError) throw updateError;
      const updated = (data as Student[] | null)?.[0];
      if (updated) onUpdated(updated);
      setNotice('결제일을 저장했어요.');
      setEditing(false);
    } catch {
      setError('결제일 저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const saveMonthlyFees = async (entries: MonthlyFeeInput[], removed: MonthlyFeeRef[]) => {
    if (entries.length > 0) {
      const { error: upsertError } = await supabase.from('tuition_monthly_fees').upsert(
        entries.map((e) => ({
          student_id: student.id,
          year: e.year,
          month: e.month,
          fee: e.fee,
          is_excluded: e.isExcluded,
        })),
        { onConflict: 'student_id,year,month' },
      );
      if (upsertError) throw upsertError;
    }
    for (const r of removed) {
      const { error: delError } = await supabase
        .from('tuition_monthly_fees')
        .delete()
        .eq('student_id', student.id)
        .eq('year', r.year)
        .eq('month', r.month);
      if (delError) throw delError;
    }
    await loadExtras();
  };

  const updateLastPaid = async (value: string | null) => {
    const { data, error: updateError } = await supabase
      .from('students')
      .update({ tuition_last_paid_at: value })
      .eq('id', student.id)
      .select()
      .maybeSingle();
    if (updateError) throw updateError;
    if (data) onUpdated(data as Student);
  };

  const changePaid = async (paid: boolean) => {
    if (!ownDueDay) {
      setError('먼저 결제일을 설정해주세요.');
      return;
    }
    setPaying(true);
    setError('');
    try {
      const targetDue = cycleDueDate(selectedKey, ownDueDay, ownOffset);
      if (paid) {
        const { error: payError } = await supabase.from('tuition_payments').upsert(
          {
            student_id: student.id,
            cycle_due: targetDue,
            amount: selFee,
            paid_at: todayKey(),
          },
          { onConflict: 'student_id,cycle_due' },
        );
        if (payError) throw payError;
        const latest = [...payments.map((p) => p.paid_at), todayKey()].sort().slice(-1)[0];
        await updateLastPaid(latest);
        await loadExtras();
        setNotice(`${cycleMonthLabel(selectedKey)} 회차를 결제완료로 기록했어요.`);
      } else {
        const { error: delError } = await supabase
          .from('tuition_payments')
          .delete()
          .eq('student_id', student.id)
          .eq('cycle_due', targetDue);
        if (delError) throw delError;
        const remaining = payments.filter((p) => p.cycle_due !== targetDue);
        const latest =
          remaining.length > 0 ? [...remaining.map((p) => p.paid_at)].sort().slice(-1)[0] : null;
        await updateLastPaid(latest);
        await loadExtras();
        setNotice(`${cycleMonthLabel(selectedKey)} 회차 결제완료를 취소했어요.`);
      }
    } catch {
      setError(paid ? '결제완료 처리에 실패했어요.' : '결제완료 취소에 실패했어요.');
    } finally {
      setPaying(false);
    }
  };

  const deletePayment = async (payment: TuitionPayment) => {
    setDeletingPaymentId(payment.id);
    setError('');
    try {
      const { error: delError } = await supabase
        .from('tuition_payments')
        .delete()
        .eq('id', payment.id);
      if (delError) throw delError;
      const remaining = payments.filter((p) => p.id !== payment.id);
      const latestPaid =
        remaining.length > 0
          ? [...remaining].map((p) => p.paid_at).sort().slice(-1)[0]
          : null;
      const wasLatest = (student.tuition_last_paid_at ?? null) === payment.paid_at;
      if (wasLatest) {
        await updateLastPaid(latestPaid);
      }
      await loadExtras();
      setNotice('납부 기록을 삭제했어요.');
    } catch {
      setError('납부 기록 삭제에 실패했어요.');
    } finally {
      setDeletingPaymentId('');
    }
  };

  const setCycleExcluded = async (key: string, value: boolean) => {
    setPaying(true);
    setError('');
    try {
      const { year, month } = parseCycleKey(key);
      const existing = monthlyFees.find((f) => f.year === year && f.month === month);
      const { error: upsertError } = await supabase.from('tuition_monthly_fees').upsert(
        {
          student_id: student.id,
          year,
          month,
          fee: existing?.fee ?? 0,
          is_excluded: value,
        },
        { onConflict: 'student_id,year,month' },
      );
      if (upsertError) throw upsertError;
      await loadExtras();
      setNotice(
        value
          ? `${cycleMonthLabel(key)} 회차를 제외했어요.`
          : `${cycleMonthLabel(key)} 회차 제외를 해제했어요.`,
      );
    } catch {
      setError('회차 제외 처리에 실패했어요.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-wallet-3-line"></i>
            </span>
            교습비
            <span
              className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.badge}`}
            >
              <i className={meta.icon}></i>
              {meta.label}
            </span>
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            월별 교습비와 매월 결제일을 관리하고, 회차별 납부 기록을 남겨요.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          className="flex items-center gap-1.5 rounded-md border border-background-300 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
        >
          <i className={editing ? 'ri-close-line' : 'ri-edit-line'}></i>
          {editing ? '취소' : '결제일 수정'}
        </button>
      </div>

      {notice && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-100/70 px-3 py-2 text-xs text-primary-700">
          <i className="ri-information-line"></i>
          {notice}
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {editing && (
        <form
          onSubmit={saveDueDay}
          className="mt-5 grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-100 p-4 sm:grid-cols-2"
        >
          <Field label="결제일 (매월 n일)">
            <select value={dueDay} onChange={(e) => setDueDay(e.target.value)} className={inputClass}>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  {n}일
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="결제 기준"
            hint="'전월'을 고르면 이 회차 결제일이 한 달 앞당겨져요."
          >
            <select
              value={dueOffset}
              onChange={(e) => setDueOffset(e.target.value)}
              className={inputClass}
            >
              <option value="0">당월 결제 · 그 달에 결제</option>
              <option value="-1">전월 결제 · 지난달에 미리 결제</option>
            </select>
          </Field>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              저장
            </button>
          </div>
        </form>
      )}

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-primary-50 p-4">
          <p className="text-xs text-foreground-500">이번 달 교습비</p>
          <p className="mt-1 text-xl font-bold text-primary-700">
            {currentExcluded ? '휴원·면제' : currentFee ? `${formatNumber(currentFee)}원` : '미입력'}
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-100 p-4">
          <p className="text-xs text-foreground-500">결제일</p>
          <p className="mt-1 text-xl font-bold text-foreground-900">
            {dueDayLabel(student.tuition_due_day, student.tuition_due_month_offset)}
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-accent-50 p-4">
          <p className="text-xs text-foreground-500">
            {status === 'overdue' ? '미납 결제일' : '다음 결제일'}
          </p>
          <p className="mt-1 text-xl font-bold text-accent-700">{formatDate(collectDue)}</p>
          <p className="mt-0.5 text-xs text-foreground-500">
            {cycleMonthLabel(collectKey)} 회차
            {collectFee > 0 ? ` · ${formatNumber(collectFee)}원` : ''}
          </p>
        </div>
      </div>

      <div
        className={`mt-3 rounded-lg border p-4 ${
          selStatus === 'overdue'
            ? 'border-accent-300 bg-accent-50'
            : 'border-background-200 bg-background-100'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedKey((k) => addCycleMonths(k, -1))}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition hover:bg-background-200"
              aria-label="이전 회차"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <div className="min-w-[128px] text-center">
              <p className="text-sm font-bold text-foreground-950">
                {cycleMonthLabel(selectedKey)} 회차
              </p>
              <p className="mt-0.5 text-[11px] text-foreground-500">
                결제 예정일 {formatDate(selDue)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedKey((k) => addCycleMonths(k, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition hover:bg-background-200"
              aria-label="다음 회차"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${selMeta.badge}`}
            >
              <i className={selMeta.icon}></i>
              {selMeta.label}
            </span>
            {selStatus === 'excluded' ? (
              <button
                type="button"
                onClick={() => setCycleExcluded(selectedKey, false)}
                disabled={paying}
                className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-200 disabled:opacity-50 whitespace-nowrap"
              >
                {paying ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-arrow-go-back-line"></i>}
                제외 해제
              </button>
            ) : (
              <>
                {isSelPaid ? (
                  <button
                    type="button"
                    onClick={() => changePaid(false)}
                    disabled={paying}
                    className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-200 disabled:opacity-50 whitespace-nowrap"
                  >
                    {paying ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-arrow-go-back-line"></i>}
                    결제완료 취소
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => changePaid(true)}
                    disabled={paying}
                    className={`flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-background-50 transition disabled:opacity-50 whitespace-nowrap ${
                      selStatus === 'overdue'
                        ? 'bg-accent-600 hover:bg-accent-700'
                        : 'bg-primary-500 hover:bg-primary-600'
                    }`}
                  >
                    {paying ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                    결제완료
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setCycleExcluded(selectedKey, true)}
                  disabled={paying}
                  className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-600 transition hover:bg-background-200 disabled:opacity-50 whitespace-nowrap"
                >
                  <i className="ri-forbid-line"></i>
                  회차 제외
                </button>
              </>
            )}
          </div>
        </div>
        <p className="mt-3 border-t border-background-200 pt-3 text-xs text-foreground-600">
          {selStatus === 'paid' &&
            `${cycleMonthLabel(selectedKey)} 교습비 ${formatNumber(selFee)}원 · 납부 완료`}
          {selStatus === 'overdue' &&
            `${cycleMonthLabel(selectedKey)} 교습비 ${formatNumber(selFee)}원 · 결제 예정일이 지났어요.`}
          {selStatus === 'upcoming' &&
            `${cycleMonthLabel(selectedKey)} 교습비 ${formatNumber(selFee)}원 · 결제 예정이에요.`}
          {selStatus === 'excluded' &&
            `${cycleMonthLabel(selectedKey)} 회차는 휴원·면제로 제외됐어요. 미납·다음 결제 계산에서 건너뛰어요.`}
          {selStatus !== 'excluded' && (
            <>
              {' '}
              다음 달분을 미리 받았다면 <i className="ri-arrow-right-s-line"></i> 로 그 달 회차를 선택해
              결제완료로 기록하세요.
            </>
          )}
        </p>
      </div>

      {loadingExtras ? (
        <div className="mt-5 flex items-center justify-center py-6 text-xs text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin"></i>
          교습비 정보를 불러오는 중...
        </div>
      ) : (
        <>
          <TuitionMonthlyFeePanel
            monthlyFees={monthlyFees}
            baseFee={baseFee}
            onSave={saveMonthlyFees}
          />
          <TuitionPaymentHistoryPanel
            payments={payments}
            deletingId={deletingPaymentId}
            onDelete={deletePayment}
          />
        </>
      )}
    </section>
  );
}